/* Libraries */
import { memo } from 'react';
import { useRouter } from 'next/router';
import cn from 'classnames';
import Link from 'next/link';

/* Components */
import { ItemWithCaret, ICON_ORIENTATION as SERVICES_ICON_PLACEMENT } from '@components/UI/MenuItem';
import { HIDE_PRODUCT_CONNECTED_HOME } from '@constants/base';
import { HT_INFO_SITELINKS } from '@constants/hellotech';

/* Query */
import useBrandingCache from '@integrations/partners/branding/useBrandingQuery.cache';

/* Constants */
import { PAGE_TYPE_FAQ, PAGE_TYPE_HOME_HUB_SURVEYS, PAGE_TYPE_PLANS, PAGE_TYPE_PRODUCTS, PAGE_TYPE_SERVICES } from '@constants/pages';
import { PartnerClient } from '@integrations/partners/partners.types';

/* Styles */
import { faqPath, homeHubPath, plansPath } from '@constants/paths';
import styles from '../standardheader.module.scss';

type CenterComponentProps = {
  serviceMenuOpen: boolean;
  setMenu: BaseEventFunction;
};

const ITEMS_ROUTES = {
  [PAGE_TYPE_SERVICES]: [/\/tech-support/],
  [PAGE_TYPE_PRODUCTS]: [/\/products/],
  [PAGE_TYPE_FAQ]: [/\/faq/],
  [PAGE_TYPE_PLANS]: [/\/plans/],
  [PAGE_TYPE_HOME_HUB_SURVEYS]: [/\/homehub/],
};

const CenterComponent = ({ serviceMenuOpen, setMenu }: CenterComponentProps) => {
  /* Hooks */
  const router = useRouter();

  /* Constants */
  const chevronStyles = styles.chevron;
  const chevronUpStyles = styles.chevronUp;
  const serviceStyles = cn('p1');
  const baseLinkStyles = cn('p1', styles.base);
  const partner = useBrandingCache() as PartnerClient;

  const activeItem = (classes: string, key: string) => {
    const s = cn(classes, {
      'font-weight-medium': (ITEMS_ROUTES as Record<string, RegExp[]>)[key].some((regex: RegExp) => regex.test(router.pathname)),
    });

    return s;
  };

  return (
    <div className={styles.centerContent}>
      <>
        <ItemWithCaret
          containerStyles={styles.servicesContainer}
          iconOrientation={SERVICES_ICON_PLACEMENT.right}
          iconName="chevron"
          iconStyles={chevronStyles}
          iconOpenStyles={chevronUpStyles}
          isOpen={serviceMenuOpen}
          onClick={setMenu}
          text="Services"
          textStyles={activeItem(serviceStyles, PAGE_TYPE_SERVICES)}
          textOpenStyles={styles.servicesActive}
          datatestid="nav-link-services"
        />
        {!partner?.hideMembershipLink && (
          <Link href={plansPath()} prefetch={false} className="-no-decoration" data-testid="nav-link-memberships">
            <span className={activeItem(baseLinkStyles, PAGE_TYPE_PLANS)}>Membership</span>
          </Link>
        )}
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={HT_INFO_SITELINKS.partnerships.url}
          className="-no-decoration"
          data-testid="nav-link-partner-with-us"
        >
          <span className={baseLinkStyles}>Partner with Us</span>
        </a>
        {!HIDE_PRODUCT_CONNECTED_HOME && (
          <Link href={homeHubPath()} prefetch={false} className="-no-decoration" data-testid="nav-link-hh">
            <span className={baseLinkStyles}>My Connected Home</span>
          </Link>
        )}
        <Link href={faqPath()} prefetch={false} className="-no-decoration" data-testid="nav-link-faq">
          <span className={activeItem(baseLinkStyles, PAGE_TYPE_FAQ)}>FAQ</span>
        </Link>
      </>
    </div>
  );
};

export default memo(CenterComponent);
